import { Group } from '@mantine/core';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import { UserRole } from '@repo/common/enums/UserRole';
import { Text } from '@repo/foundations';
import { useAuthUser } from '../../../api';
import { CustomSelector } from '../../CustomSelector';

function RoleSelect(props: {
	role: UserRole;
	disabled?: boolean;
	selectRole: (role: UserRole) => void;
	withDescriptions?: boolean;
	legacyStyle?: boolean;
}) {
	const {
		role,
		disabled,
		selectRole,
		withDescriptions,
		legacyStyle = true,
	} = props;
	const { user: loggedInUser } = useAuthUser();

	let roles = [...editorPlanRoles, ...viewerPlanRoles];
	// GUEST role has been removed

	const roleOptions = roles.map((r) => ({
		label: r.title,
		value: r.key,
		description: r.description,
		disabled: !(
			privilegeMap[loggedInUser.role as UserRole] ||
			privilegeMap[UserRole.ADMIN as UserRole]
		)
			// @ts-ignore
			.includes(r.key),
	}));

	return (
		<>
			{legacyStyle ? (
				<CustomSelector
					disabled={disabled}
					items={roleOptions}
					menuProps={{
						width: 'target',
					}}
					renderItem={(option) => (
						<Group
							px={12}
							py={6}
							sx={{ flexGrow: 1, flexShrink: 1, flexWrap: 'nowrap' }}
						>
							{!withDescriptions && <Text size="sm">{option.label}</Text>}
							{withDescriptions && (
								<Text size="sm">
									<strong>{option.label}</strong> - {option.description}
								</Text>
							)}
						</Group>
					)}
					value={role}
					onChange={(value) => {
						selectRole(value as UserRole);
					}}
				/>
			) : (
				<SingleSelector
					variant="tertiary"
					searchable
					property="role"
					options={roleOptions}
					onChange={(value) => {
						selectRole(value as UserRole);
					}}
					isViewerUser={disabled ?? false}
					initialSelected={role}
					readOnly={disabled}
					iconType="none"
					rightIconName="selector"
				/>
			)}
		</>
	);
}

export default RoleSelect;

const editorPlanRoles = [
	{
		title: 'Admin',
		description: 'Full administrative access',
		key: UserRole.ADMIN,
		default: true,
	},
	{
		title: 'Editor',
		description: 'Edit access with limited permissions',
		key: UserRole.EDITOR,
		default: false,
	},
];

const viewerPlanRoles = [
	{
		title: 'Viewer',
		description: 'Read only access and can post questions',
		key: UserRole.VIEWER,
		default: false,
	},
];

// GUEST role has been removed

const privilegeMap: Record<UserRole, UserRole[]> = {
	[UserRole.ADMIN]: [
		UserRole.ADMIN,
		UserRole.EDITOR,
		UserRole.VIEWER,
		UserRole.CUSTOM,
	],
	[UserRole.EDITOR]: [UserRole.EDITOR, UserRole.VIEWER],
	[UserRole.VIEWER]: [UserRole.VIEWER],
	[UserRole.CUSTOM]: [UserRole.CUSTOM],
	[UserRole.GUEST]: [], // Kept for backward compatibility
};
